import { EventEmitter } from 'eventemitter3';
import { proxy } from 'valtio';
import { parseUpdateMeta } from 'yjs';

// todo: https://product-fabric.atlassian.net/browse/CNS-10273 - Remove dependency
// eslint-disable-next-line no-restricted-imports
import { currentPendingChanges } from '@canvas/canvas-state/pending-changes';
// todo: https://product-fabric.atlassian.net/browse/CNS-10273 - Remove dependency
// eslint-disable-next-line no-restricted-imports
import { currentWebsocketDebugState } from '@canvas/canvas-state/websocket-debug';
import { type PendingMessages } from '@canvas/model/network';

export function* changesetToIds(
  clientId: number,
  changeSet: ReturnType<typeof parseUpdateMeta>
): Generator<number> {
  const start = changeSet.from.get(clientId);
  const end = changeSet.to.get(clientId);

  if (start !== undefined && end !== undefined) {
    for (let i = start; i < end; i++) {
      yield i;
    }
  }
}

type DroppedCallback = (ids: string[]) => void;
type RoundtripCallback = (sentAt: number, receivedAt: number) => void;

export class UpdateTracker implements PendingMessages {
  private readonly events = new EventEmitter();
  private polling?: NodeJS.Timeout;

  public readonly pending: Map<number, number> = new Map();

  public readonly pendingMetadata = proxy({
    areChangesPending: false,
  });

  constructor(private readonly clientId: number) {}

  private onPoll = () => {
    if (this.hasDroppedMessages) {
      this.events.emit('dropped');
    }
  };

  sent(update: Uint8Array) {
    const updateMeta = parseUpdateMeta(update);

    for (const change of changesetToIds(this.clientId, updateMeta)) {
      this.pending.set(change, Date.now());
    }

    this.recomputeState();
  }

  received(update: Uint8Array) {
    const updateMeta = parseUpdateMeta(update);
    const receivedAt = Date.now();

    for (const change of changesetToIds(this.clientId, updateMeta)) {
      const sentAt = this.pending.get(change);
      if (sentAt !== undefined) {
        this.events.emit('roundtrip', sentAt, receivedAt);
      }
      this.pending.delete(change);
    }

    this.recomputeState();
  }

  recomputeState() {
    this.pendingMetadata.areChangesPending = this.pending.size > 0;
    currentPendingChanges.areChangesPending = this.pendingMetadata.areChangesPending;
    if ((globalThis as any).host !== undefined) {
      const host = (globalThis as any).host;
      host.isSynchronized = !this.pendingMetadata.areChangesPending;
    }
  }

  reset() {
    this.pending.clear();
    this.recomputeState();
  }

  on(event: 'dropped', fn: DroppedCallback): this;
  on(event: 'roundtrip', fn: RoundtripCallback): this;
  on(event: 'dropped' | 'roundtrip', fn: DroppedCallback | RoundtripCallback): this {
    if (event === 'dropped' && !this.polling) {
      this.polling = setInterval(this.onPoll, 1000);
    }

    this.events.on(event, fn);
    return this;
  }

  off(event: 'dropped', fn: DroppedCallback): this;
  off(event: 'roundtrip', fn: RoundtripCallback): this;
  off(event: 'dropped' | 'roundtrip', fn: DroppedCallback | RoundtripCallback): this {
    this.events.off(event, fn);

    if (
      event === 'dropped' &&
      this.polling &&
      this.events.listenerCount('dropped') === 0
    ) {
      clearInterval(this.polling);
    }

    return this;
  }

  get areChangesPending(): boolean {
    return this.pendingMetadata.areChangesPending;
  }

  get timeSinceSyncMs(): number | undefined {
    if (this.areChangesPending) {
      let oldestPendingChange = Number.POSITIVE_INFINITY;

      for (const sentAt of this.pending.values()) {
        oldestPendingChange = Math.min(oldestPendingChange, sentAt);
      }

      return Date.now() - oldestPendingChange;
    }
  }

  get hasDroppedMessages(): boolean {
    if (!this.areChangesPending) {
      return false;
    }

    const now = Date.now();

    for (const time of this.pending.values()) {
      if (now - time > currentWebsocketDebugState.dropTimeoutMs) {
        return true;
      }
    }

    return false;
  }
}
